import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import api from "services/api";
import auth from "services/auth";
import { __ } from "services/Translator";
import AuthGate from "components/auth/AuthGate";
import MissionHubContent from "components/missions/MissionHubContent";
import MissionsTeaser from "components/missions/MissionsTeaser";
import { CloverIcon } from "components/missions/missionIcons";
import { normalizeMissionsHub } from "components/missions/missionsNormalize";
import { getMissionsMockState } from "components/missions/missionsMockData";

// Dev-only fixture switch. A fabricated personal dashboard must NEVER reach an
// end user, so the handoff mock is opt-in behind an explicit build flag. In
// production the hub renders real data or a graceful "coming soon" state.
const ALLOW_PREVIEW_FIXTURE =
  process.env.REACT_APP_MISSIONS_PREVIEW === "true";

function fallbackPlayerName() {
  const user = auth.getUser() || {};
  return user.username || user.first_name || "";
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Self-contained gold shimmer so the loading state never depends on new SCSS.
// Reuses the `shimmer` keyframe already declared in _missions.scss.
function skeletonStyle(extra) {
  const base = {
    borderRadius: "10px",
    background:
      "linear-gradient(90deg, rgba(248,216,112,0.05) 25%, rgba(248,216,112,0.16) 50%, rgba(248,216,112,0.05) 75%)",
    backgroundSize: "200% 100%",
  };
  if (!prefersReducedMotion()) {
    base.animation = "shimmer 1.6s linear infinite";
  }
  return { ...base, ...extra };
}

function SkeletonBar({ w = "100%", h = 12, style }) {
  return (
    <span
      className="mission-hub__skeleton-bar"
      aria-hidden="true"
      style={skeletonStyle({ display: "block", width: w, height: h, ...style })}
    />
  );
}

function MissionHubSkeleton() {
  return (
    <div
      className="wheel_container mission-hub container-xxl mission-hub--loading"
      aria-busy="true"
    >
      <div className="page mission-hub__inner">
        <p
          className="mission-hub__preview-note"
          role="status"
          style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}
        >
          {__("Loading mission hub...")}
        </p>

        <section className="vip_banner hub" aria-hidden="true">
          <div className="ttl" style={{ display: "flex", gap: "14px", alignItems: "center" }}>
            <SkeletonBar w={48} h={48} style={{ borderRadius: "12px", flex: "0 0 auto" }} />
            <div style={{ flex: 1, display: "grid", gap: "10px" }}>
              <SkeletonBar w="40%" h={20} />
              <SkeletonBar w="70%" h={12} />
            </div>
          </div>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="metric" style={{ display: "grid", gap: "8px", justifyItems: "center" }}>
              <SkeletonBar w={40} h={22} />
              <SkeletonBar w={64} h={10} />
            </div>
          ))}
        </section>

        <section className="vip_club_area mission-featured" aria-hidden="true">
          <SkeletonBar w="100%" h={120} style={{ borderRadius: "14px", marginBottom: "14px" }} />
          <SkeletonBar w="55%" h={18} style={{ marginBottom: "10px" }} />
          <SkeletonBar w="85%" h={12} style={{ marginBottom: "16px" }} />
          <SkeletonBar w="45%" h={40} style={{ borderRadius: "999px" }} />
        </section>

        <div className="hub-grid">
          <section className="vip_club_area log" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="log-item"
                style={{ display: "flex", gap: "14px", alignItems: "center", marginBottom: "12px" }}
              >
                <SkeletonBar w={64} h={64} style={{ borderRadius: "12px", flex: "0 0 auto" }} />
                <div style={{ flex: 1, display: "grid", gap: "9px" }}>
                  <SkeletonBar w="50%" h={14} />
                  <SkeletonBar w="80%" h={10} />
                  <SkeletonBar w="100%" h={8} style={{ borderRadius: "999px" }} />
                </div>
                <SkeletonBar w={72} h={36} style={{ borderRadius: "999px", flex: "0 0 auto" }} />
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
}

function MissionHubEmpty() {
  const history = useHistory();
  return (
    <div className="wheel_container mission-hub container-xxl">
      <div className="page mission-hub__inner">
        <section className="vip_club_area mission-hub__coming-soon">
          <span className="mission-hub__coming-soon-ico" aria-hidden="true">
            <CloverIcon size={30} />
          </span>
          <h2>{__("Missions are on their way")}</h2>
          <p>{__("New daily, weekly and communal missions drop here soon. Keep playing to be ready.")}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => history.push("/games/all")}
          >
            {__("Browse games")}
          </button>
        </section>
      </div>
    </div>
  );
}

function MissionsHub() {
  const [loading, setLoading] = useState(true);
  const [hubData, setHubData] = useState(null);
  const [isPreviewDemo, setIsPreviewDemo] = useState(false);

  const loadHub = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/challenges/hub");
      if (response.status === 200 && response.data?.data) {
        setHubData(normalizeMissionsHub(response.data.data, fallbackPlayerName()));
        setIsPreviewDemo(false);
        return;
      }
    } catch (err) {
      // API not ready.
    }
    if (ALLOW_PREVIEW_FIXTURE) {
      setHubData(getMissionsMockState());
      setIsPreviewDemo(true);
    } else {
      setHubData(null);
      setIsPreviewDemo(false);
    }
  }, []);

  // POST /challenges/{code}/opt-in | /claim; the response carries a fresh hub.
  // api.post never rejects — failures come back as {status, data}, so success
  // is only status 200 AND a hub payload; anything else is reported as failure.
  const performChallengeAction = useCallback(async (code, action) => {
    const response = await api.post(`/challenges/${code}/${action}`, {});
    const payload = response?.data?.data;
    const ok = response?.status === 200 && !!payload?.hub;
    if (ok) {
      setHubData(normalizeMissionsHub(payload.hub, fallbackPlayerName()));
    }
    return {
      ok,
      message: response?.data?.message || payload?.message || "",
      hub: ok ? payload.hub : null,
    };
  }, []);

  useEffect(() => {
    loadHub().finally(() => setLoading(false));
  }, [loadHub]);

  if (loading) {
    return <MissionHubSkeleton />;
  }

  if (!hubData) {
    return <MissionHubEmpty />;
  }

  return (
    <MissionHubContent
      data={hubData}
      isPreviewDemo={isPreviewDemo}
      onChallengeAction={isPreviewDemo ? null : performChallengeAction}
    />
  );
}

export default function Missions(props) {
  return (
    <AuthGate teaser={<MissionsTeaser {...props} />}>
      <MissionsHub />
    </AuthGate>
  );
}
